import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Building2, Truck, Home } from 'lucide-react';

export const pqrsColumns = [
  {
    id: 'consecutivo',
    key: 'consecutivo',
    label: 'Consecutivo',
    type: 'text',
    required: false,
    hidden: true,
    width: 'w-32'
  },
  {
    id: 'tipo',
    key: 'tipo',
    label: 'Tipo',
    type: 'select',
    required: true,
    hidden: false,
    options: [
      { value: 'Petición', label: 'Petición' },
      { value: 'Queja', label: 'Queja' },
      { value: 'Reclamo', label: 'Reclamo' }
    ],
    width: 'w-32',
    render: (row) => {
      const colors = {
        'Petición': 'bg-blue-100 text-blue-800',
        'Queja': 'bg-yellow-100 text-yellow-800',
        'Reclamo': 'bg-red-100 text-red-800'
      };
      return <Badge className={colors[row.tipo] || 'bg-gray-100 text-gray-800'}>{row.tipo}</Badge>;
    }
  },
  {
    id: 'categoria',
    key: 'categoria',
    label: 'Categoría',
    type: 'select',
    required: true,
    hidden: false,
    options: [
      { value: 'Logística', label: 'Logística' },
      { value: 'Calidad del lavado', label: 'Calidad del lavado' },
      { value: 'Entregas', label: 'Entregas' },
      { value: 'Faltantes', label: 'Faltantes' },
      { value: 'Reprocesos', label: 'Reprocesos' },
      { value: 'Insumos', label: 'Insumos' },
      { value: 'Otro', label: 'Otro' }
    ],
    width: 'w-40',
    render: (row) => {
      const colors = {
        'Logística': 'bg-purple-100 text-purple-800',
        'Calidad del lavado': 'bg-green-100 text-green-800',
        'Entregas': 'bg-blue-100 text-blue-800',
        'Faltantes': 'bg-orange-100 text-orange-800',
        'Reprocesos': 'bg-pink-100 text-pink-800',
        'Insumos': 'bg-teal-100 text-teal-800',
        'Otro': 'bg-gray-100 text-gray-800'
      };
      return <Badge className={colors[row.categoria] || 'bg-gray-100 text-gray-800'}>{row.categoria}</Badge>;
    }
  },
  {
    id: 'origen',
    key: 'origen',
    label: 'Origen',
    type: 'object',
    required: true,
    hidden: false,
    width: 'w-40',
    render: (row) => {
      if (!row.origen) return '-';
      
      const { tipo } = row.origen;
      
      const icons = {
        'Lavandería': <Building2 className="h-4 w-4" />,
        'Ruta / Conductor': <Truck className="h-4 w-4" />,
        'Apartamento': <Home className="h-4 w-4" />
      };
      
      const colors = {
        'Lavandería': 'bg-purple-100 text-purple-800',
        'Ruta / Conductor': 'bg-blue-100 text-blue-800',
        'Apartamento': 'bg-green-100 text-green-800'
      };
      
      return (
        <Badge className={`${colors[tipo] || 'bg-gray-100 text-gray-800'} flex items-center gap-1 w-fit`}>
          {icons[tipo]}
          {tipo}
        </Badge>
      );
    }
  },
  {
    id: 'apartamento',
    key: 'origen',
    label: 'Apartamento',
    type: 'object',
    required: false,
    hidden: false,
    width: 'w-48',
    render: (row) => {
      if (!row.origen) return '-';
      
      const { propiedad, apartamento } = row.origen;
      
      if (!propiedad && !apartamento) return '-';
      
      return (
        <div className="text-xs space-y-0.5">
          {propiedad?.edificio && (
            <div className="font-medium text-gray-700">
              {propiedad.edificio.nombre}
              {propiedad.edificio.ciudad && <span className="text-gray-500"> • {propiedad.edificio.ciudad}</span>}
            </div>
          )}
          {apartamento && (
            <div className="text-gray-600">
              Apto: {apartamento}
            </div>
          )}
        </div>
      );
    }
  },
  {
    id: 'descripcion',
    key: 'descripcion',
    label: 'Descripción',
    type: 'textarea',
    required: true,
    hidden: false,
    placeholder: 'Describa detalladamente el incidente...',
    width: 'w-full',
    render: (row) => {
      if (!row.descripcion) return '-';
      return row.descripcion.length > 100 ? `${row.descripcion.substring(0, 100)}...` : row.descripcion;
    }
  },
  {
    id: 'prioridad',
    key: 'prioridad',
    label: 'Prioridad',
    type: 'select',
    required: true,
    hidden: false,
    options: [
      { value: 'Alta', label: 'Alta' },
      { value: 'Media', label: 'Media' },
      { value: 'Baja', label: 'Baja' }
    ],
    width: 'w-28',
    render: (row) => {
      const colors = {
        'Alta': 'bg-red-100 text-red-800',
        'Media': 'bg-yellow-100 text-yellow-800',
        'Baja': 'bg-green-100 text-green-800'
      };
      return <Badge className={colors[row.prioridad] || 'bg-gray-100 text-gray-800'}>{row.prioridad}</Badge>;
    }
  },
  {
    id: 'estado',
    key: 'estado',
    label: 'Estado',
    type: 'select',
    required: false,
    options: [
      { value: 'Abierta', label: 'Abierta' },
      { value: 'En proceso', label: 'En proceso' },
      { value: 'En espera', label: 'En espera' },
      { value: 'Cerrada', label: 'Cerrada' }
    ],
    width: 'w-32',
    render: (row) => {
      const colors = {
        'Abierta': 'bg-red-100 text-red-800',
        'En proceso': 'bg-blue-100 text-blue-800',
        'En espera': 'bg-yellow-100 text-yellow-800',
        'Cerrada': 'bg-green-100 text-green-800'
      };
      return <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>{row.estado}</Badge>;
    }
  },
  {
    id: 'usuarioReporta',
    key: 'usuarioReporta',
    label: 'Usuario Reporta',
    type: 'object',
    required: true,
    hidden: false,
    width: 'w-40',
    render: (row) => {
      if (!row.usuarioReporta) return '-';
      return row.usuarioReporta.nombre || '-';
    }
  },
  {
    id: 'fechaRegistro',
    key: 'fechaRegistro',
    label: 'Fecha Registro',
    type: 'date',
    required: false,
    hidden: false,
    width: 'w-36',
    render: (row) => {
      if (!row.fechaRegistro) return '-';
      try {
        return format(new Date(row.fechaRegistro), 'dd/MM/yyyy HH:mm', { locale: es });
      } catch {
        return '-';
      }
    }
  },
  {
    id: 'fechaCierre',
    key: 'fechaCierre',
    label: 'Fecha Cierre',
    type: 'date',
    required: false,
    hidden: true,
    width: 'w-36',
    render: (row) => {
      if (!row.fechaCierre) return '-';
      try {
        return format(new Date(row.fechaCierre), 'dd/MM/yyyy HH:mm', { locale: es });
      } catch {
        return '-';
      }
    }
  },
  {
    id: 'solucion',
    key: 'solucion',
    label: 'Solución',
    type: 'textarea',
    required: false,
    hidden: true,
    placeholder: 'Describa la solución aplicada...',
    width: 'w-full'
  },
  {
    id: 'actions',
    key: 'actions',
    label: 'Acciones',
    width: 'w-32',
    actions: [
      {
        key: 'view',
        label: 'Ver',
        icon: 'Eye',
        tooltip: 'Ver detalles',
        className: '',
        showWhen: (row) => row.estado === 'Cerrada'
      },
      {
        key: 'manage',
        label: 'Gestionar',
        icon: 'MessageSquare',
        tooltip: 'Gestionar PQR',
        className: '',
        showWhen: (row) => row.estado !== 'Cerrada'
      }
    ]
  }
];

export const columnsExcel = [
  { header: 'Consecutivo', key: 'consecutivo' },
  { header: 'Tipo', key: 'tipo' },
  { header: 'Categoría', key: 'categoria' },
  { header: 'Origen', key: 'origen.tipo' },
  { header: 'Propiedad', key: 'origen.propiedad.nombre' },
  { header: 'Apartamento', key: 'origen.apartamento' },
  { header: 'Descripción', key: 'descripcion' },
  { header: 'Prioridad', key: 'prioridad' },
  { header: 'Estado', key: 'estado' },
  { header: 'Usuario Reporta', key: 'usuarioReporta.nombre' },
  { header: 'Responsable', key: 'responsable.nombre' },
  { header: 'Fecha Registro', key: 'fechaRegistro' },
  { header: 'Fecha Cierre', key: 'fechaCierre' },
  { header: 'Solución', key: 'solucion' }
];
